import { createLogger } from 'shared/services/logger';
import {Point, Size} from '../objects/';
import {array2DCreate} from '../utility/helpers';

const logger = createLogger('UScope:lib:SearchService');

//    This file contains the Search object which implements sequencing through a region in a
//            pattern of motion defined by ePattern.
//            The region coordinate system is left,top of 0,0; positive x to the right;
//            positive y down.  Hence all return points will have positive coordinates (except the
//            last, end-of-search, point).
//
//            To start search create a Search object with the starting coordinate, region and pattern
//            to sequence.  Then call Next() to get each point in succession until the search has
//            completed as indicated by an empty return value.

export const SEARCH_PATTERN = {
  NONE: 0,
  RASTER: 1,
  SPIRAL_FILL: 2,
  SPIRAL_FRAME: 3,
  RIGHT_DOWN: 4,
  RIGHT_UP: 5,
  DOWN_RIGHT: 6,
  DOWN_LEFT: 7,
  SNAKE_RIGHT_DOWN: 8,
  SNAKE_DOWN_RIGHT: 9
};

export function SearchService (cpStart, csRegion, ePattern) {
  if (ePattern === undefined) {
    logger.warn('Search: undefined search pattern');
    return;
  }
  const m_cpCurrent = new Point(cpStart.x, cpStart.y);
  let m_csRegion = new Size(csRegion.w, csRegion.h);
  let m_ePattern = ePattern;
  let m_bInProgress = false;
  let m_bSearchIsComplete = false;
  let m_nCurCount = 0;
  let m_nMaxCount = m_csRegion.getArea();
  let m_nRadius = 0;
  let m_nDirCurrent = 0;
  let m_nDirCountDown = 0;
  let m_nDirSecondary = 0;

  // prohibit processing if settings aren't reasonable
  if (m_csRegion.w < 1 || m_csRegion.h < 1 || m_cpCurrent.x < 0 || m_cpCurrent.y < 0) {
    m_ePattern = SEARCH_PATTERN.NONE;
    m_bSearchIsComplete = true;
  }

  // spiral motion is in a clockwise direction as defined by this order
  const eSPIRAL_DIR = {eMOVE_RIGHT: 0, eMOVE_DOWN: 1, eMOVE_LEFT: 2, eMOVE_UP: 3};
  const m_cpPropDirInc = array2DCreate(2, 4); // index [0] is for X direction; index [1] is Y
  m_cpPropDirInc[0][eSPIRAL_DIR.eMOVE_RIGHT] = +1;
  m_cpPropDirInc[1][eSPIRAL_DIR.eMOVE_RIGHT] = 0;
  m_cpPropDirInc[0][eSPIRAL_DIR.eMOVE_DOWN] = 0;
  m_cpPropDirInc[1][eSPIRAL_DIR.eMOVE_DOWN] = +1;
  m_cpPropDirInc[0][eSPIRAL_DIR.eMOVE_LEFT] = -1;
  m_cpPropDirInc[1][eSPIRAL_DIR.eMOVE_LEFT] = 0;
  m_cpPropDirInc[0][eSPIRAL_DIR.eMOVE_UP] = 0;
  m_cpPropDirInc[1][eSPIRAL_DIR.eMOVE_UP] = -1;

  switch (m_ePattern) {
    case SEARCH_PATTERN.SPIRAL_FILL:
      // because the spiral can be started anywhere in the region
      // the algorithm insures that it covers the region by allowing spiraling
      // out of and then back into the region.  The worst case total count must is computed as safety stopping criteria.
      m_csRegion = new Size(csRegion.w - 1, csRegion.h - 1); // search is inclusive of the last row and col so redefine region w,h
      m_nRadius = 0;
      const nXRadius = Math.max(m_csRegion.w - m_cpCurrent.x + 1, m_csRegion.w);
      const nYRadius = Math.max(m_csRegion.h - m_cpCurrent.y + 1, m_csRegion.h);
      const nDiamMax = Math.max(2 * nXRadius, 2 * nYRadius);
      m_nMaxCount = nDiamMax * nDiamMax;
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_UP;
      m_nDirCountDown = 1;
      break;
    case SEARCH_PATTERN.SPIRAL_FRAME:
      //  the sprial frame starts one to the right of the diagonal inner boundary
      m_csRegion = new Size(csRegion.w - 1, csRegion.h - 1); // search is inclusive of the last row and col so redefine region w,h
      m_nRadius = m_csRegion.w - 2 * m_cpCurrent.x - 1;
      m_cpCurrent.x++; // start one step to the right but requries input diagonal ...
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_UP;
      m_nDirCountDown = 1; // so first entry switches to direction right
      break;
    case SEARCH_PATTERN.RIGHT_DOWN:
    case SEARCH_PATTERN.RIGHT_UP:
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_RIGHT;
      m_nDirSecondary = (m_ePattern == SEARCH_PATTERN.RIGHT_DOWN) ? eSPIRAL_DIR.eMOVE_DOWN : eSPIRAL_DIR.eMOVE_UP;
      break;
    case SEARCH_PATTERN.DOWN_RIGHT:
    case SEARCH_PATTERN.DOWN_LEFT:
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_DOWN;
      m_nDirSecondary = (m_ePattern == SEARCH_PATTERN.DOWN_RIGHT) ? eSPIRAL_DIR.eMOVE_RIGHT : eSPIRAL_DIR.eMOVE_LEFT;
      break;
    case SEARCH_PATTERN.SNAKE_RIGHT_DOWN:
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_RIGHT; // and left....
      m_nDirSecondary = eSPIRAL_DIR.eMOVE_DOWN;
      break;
    case SEARCH_PATTERN.SNAKE_DOWN_RIGHT:
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_DOWN; // and up....
      m_nDirSecondary = eSPIRAL_DIR.eMOVE_RIGHT;
      break;
  }

  // ............................................ get next point in pattern - PUBLIC
  this.next = function () {
    if (m_bSearchIsComplete) {
      return;
    }

    if (m_bInProgress == false) { // if just starting, return current point
      m_bInProgress = true;
      return new Point(m_cpCurrent.x, m_cpCurrent.y);
    }

    switch (m_ePattern) { // otherwise call on the pattern sequencing method
      case SEARCH_PATTERN.SPIRAL_FILL:
        return nextSpiral();
      case SEARCH_PATTERN.SPIRAL_FRAME:
        return nextFrame();
      case SEARCH_PATTERN.RIGHT_DOWN:
      case SEARCH_PATTERN.RIGHT_UP:
        return nextRasterX();
      case SEARCH_PATTERN.DOWN_RIGHT:
      case SEARCH_PATTERN.DOWN_LEFT:
        return nextRasterY();
      case SEARCH_PATTERN.SNAKE_RIGHT_DOWN:
        return nextSnakeX();
      case SEARCH_PATTERN.SNAKE_DOWN_RIGHT:
        return nextSnakeY();
      default:
    }
  };
  // ............................................ get next point in snake X direction pattern - PRIVATE
  function nextSnakeX () {
    if (m_nCurCount > m_nMaxCount) { // is it time to stop
      m_bSearchIsComplete = true;
      return;
    }
    m_nCurCount++;
    m_cpCurrent.x += m_cpPropDirInc[0][m_nDirCurrent];

    if (m_nDirCurrent == eSPIRAL_DIR.eMOVE_RIGHT && m_cpCurrent.x >= m_csRegion.w) {
      m_cpCurrent.x = m_csRegion.w - 1; // x resets to 0
      m_cpCurrent.y += m_cpPropDirInc[1][m_nDirSecondary]; // move y up or down
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_LEFT; // and switch right motion for left
    } else if (m_nDirCurrent == eSPIRAL_DIR.eMOVE_LEFT && m_cpCurrent.x < 0) {
      m_cpCurrent.x = 0; // x resets to 0
      m_cpCurrent.y += m_cpPropDirInc[1][m_nDirSecondary]; // move y up or down
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_RIGHT; // and switch right motion for left
    }
    if (m_cpCurrent.y < m_csRegion.h && m_cpCurrent.y >= 0) {
      return new Point(m_cpCurrent);
    }
  }

  function nextSnakeY () {
    if (m_nCurCount > m_nMaxCount) { // is it time to stop
      m_bSearchIsComplete = true;
      return;
    }
    m_nCurCount++;
    m_cpCurrent.y += m_cpPropDirInc[1][m_nDirCurrent];

    if (m_nDirCurrent == eSPIRAL_DIR.eMOVE_DOWN && m_cpCurrent.y >= m_csRegion.h) {
      m_cpCurrent.y = m_csRegion.h - 1;
      m_cpCurrent.x += m_cpPropDirInc[0][m_nDirSecondary];
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_UP;
    } else if (m_nDirCurrent == eSPIRAL_DIR.eMOVE_UP && m_cpCurrent.y < 0) {
      m_cpCurrent.y = 0;
      m_cpCurrent.x += m_cpPropDirInc[0][m_nDirSecondary];
      m_nDirCurrent = eSPIRAL_DIR.eMOVE_DOWN;
    }

    if (m_cpCurrent.x < m_csRegion.w && m_cpCurrent.x >= 0) {
      return new Point(m_cpCurrent);
    }
  }

  function nextSpiral () {
    do {
      if (++m_nCurCount > m_nMaxCount) { // is it time to stop?
        m_bSearchIsComplete = true;
        return;
      }

      m_nDirCountDown--;
      if (!m_nDirCountDown) { // if its time to switch directions
        m_nDirCurrent++;
        if (m_nDirCurrent > eSPIRAL_DIR.eMOVE_UP) { m_nDirCurrent = eSPIRAL_DIR.eMOVE_RIGHT; }

        if (m_nDirCurrent == eSPIRAL_DIR.eMOVE_RIGHT || m_nDirCurrent == eSPIRAL_DIR.eMOVE_LEFT) { m_nRadius++; }
        m_nDirCountDown = m_nRadius;
      }
      m_cpCurrent.add(m_cpPropDirInc[0][m_nDirCurrent], m_cpPropDirInc[1][m_nDirCurrent]);
    } while (m_cpCurrent.x < 0 || m_cpCurrent.x > m_csRegion.w || m_cpCurrent.y < 0 || m_cpCurrent.y > m_csRegion.h);

    return new Point(m_cpCurrent);
  }

  function nextFrame () {
    do {
      if (++m_nCurCount > m_nMaxCount) { // is it time to stop
        m_bSearchIsComplete = true;
        return;
      }

      m_nDirCountDown--;
      if (m_nDirCountDown <= 0) { // if its time to switch directions
        m_nDirCurrent++;
        if (m_nDirCurrent > eSPIRAL_DIR.eMOVE_UP) { m_nDirCurrent = eSPIRAL_DIR.eMOVE_RIGHT; }

        if (m_nDirCurrent == eSPIRAL_DIR.eMOVE_UP || m_nDirCurrent == eSPIRAL_DIR.eMOVE_DOWN) { m_nRadius++; }
        m_nDirCountDown = m_nRadius;
      }
      m_cpCurrent.add(m_cpPropDirInc[0][m_nDirCurrent], m_cpPropDirInc[1][m_nDirCurrent]);
    } while (m_cpCurrent.x < 0 || m_cpCurrent.x > m_csRegion.w || m_cpCurrent.y < 0 || m_cpCurrent.y > m_csRegion.h);

    return new Point(m_cpCurrent);
  }

  function nextRasterX () {
    if (m_nCurCount > m_nMaxCount) { // is it time to stop
      m_bSearchIsComplete = true;
      return;
    }
    m_nCurCount++;
    m_cpCurrent.x += m_cpPropDirInc[0][m_nDirCurrent];
    if (m_cpCurrent.x >= m_csRegion.w) { // if too far right
      m_cpCurrent.x = 0; // x resets to 0
      m_cpCurrent.y += m_cpPropDirInc[1][m_nDirSecondary]; // move y up or down
    }
    if (m_cpCurrent.y < m_csRegion.h && m_cpCurrent.y >= 0) {
      return new Point(m_cpCurrent);
    }
  }

  function nextRasterY () {
    if (m_nCurCount > m_nMaxCount) { // is it time to stop
      m_bSearchIsComplete = true;
      return;
    }
    m_nCurCount++;
    m_cpCurrent.y += m_cpPropDirInc[1][m_nDirCurrent];
    if (m_cpCurrent.y >= m_csRegion.h) {
      m_cpCurrent.y = 0;
      m_cpCurrent.x += m_cpPropDirInc[0][m_nDirSecondary];
    }
    if (m_cpCurrent.x < m_csRegion.w && m_cpCurrent.x >= 0) {
      return new Point(m_cpCurrent);
    }
  }
}



// WEBPACK FOOTER //
// ./src/uScope/js/lib/services/search-service.js